import { BaseError } from '@iotize/common/error';

import { InvalidConfigurationError } from './invalid-configuration-error';
import { IotizeBundleModel } from './iotize-bundle-model';
import { IotizeProfileModel } from './iotize-profile-model';
import { IotizeVariableModel } from './iotize-variable-model';

/**
 *
 */
export class BundleDoesNotExistError extends BaseError {
  // static CODE = 'BundleDoesNotExistError';

  constructor(
    public bundleId: string,
    public config: IotizeConfigModel,
  ) {
    super(
      'Bundle "' + bundleId + '" does not exist.',
      // BundleDoesNotExistError.CODE
    );
  }
}

/**
 *
 */
export class VariableDoesNotExistError extends BaseError {
  // static CODE = 'VariableDoesNotExistError';

  constructor(
    public variableId: string,
    public config: IotizeConfigModel,
  ) {
    super(
      `Variable "${variableId}" does not exist.`,
      // VariableDoesNotExistError.CODE
    );
  }
}

/**
 * Class representing an iotize studio configuration for a module
 */
export class IotizeConfigModel {
  /**
   * Iotize studio configuration JSON formatted
   */
  public data: any;

  constructor(data: any) {
    if (!data.IOTizeConfiguration) {
      //console.error('Trying to create an IotizeConfigModel with invalid data: ', data);
      throw new InvalidConfigurationError(
        'Key IOTizeConfiguration must exists',
      );
    }
    if (!data.IOTizeConfiguration.IOTizeApp) {
      throw new InvalidConfigurationError(
        'Key IOTizeConfiguration.IOTizeApp must exists',
      );
    }
    if (
      data.IOTizeConfiguration.IOTizeApp.length === 0 ||
      !data.IOTizeConfiguration.IOTizeApp[0]['$']
    ) {
      throw new InvalidConfigurationError(
        'No IOTizeConfiguration.IOTizeApp defined in this configuration file',
      );
    }
    this.data = data;
  }

  public toJson(): any {
    return this.data;
  }

  private getAppConfig() {
    return this.data.IOTizeConfiguration.IOTizeApp[0];
  }

  /**
   * Get application name
   */
  public appName(): string {
    if (!this.getAppConfig().$.Name) {
      throw new InvalidConfigurationError('Application Name is not set');
    }
    return this.getAppConfig().$.Name;
  }

  /**
   * Get config version
   */
  public version(): string {
    if (!this.getAppConfig().$.Config) {
      throw new InvalidConfigurationError('App config version is not set');
    }
    return this.getAppConfig().$.Config;
  }

  /**
   * Get module serial number
   */
  public id(): string {
    if (!this.getAppConfig().$.DevIoTizeSN) {
      throw new InvalidConfigurationError('Serial number is not set');
    }
    return this.getAppConfig().$.DevIoTizeSN;
  }

  /**
   * Set module serial number
   * @param id
   */
  public setId(id: string): IotizeConfigModel {
    this.getAppConfig().$.DevIoTizeSN = id;
    return this;
  }

  /**
   * Alias for this.id();
   */
  public serialNumber(): string {
    return this.id();
  }

  /**
   * Get an array of all bundles
   */
  public bundles(): IotizeBundleModel[] {
    if (!this.data.IOTizeConfiguration.IOTizeBundle) {
      return [];
    }
    let bundles = [];
    for (let bundle of this.data.IOTizeConfiguration.IOTizeBundle) {
      // TODO bug if only one bundle
      bundles.push(new IotizeBundleModel(bundle, this));
    }
    return bundles;
  }

  /**
   * Array of bundles available for a specific profile
   */
  public bundleAvailableForProfileName(
    profileName: string,
  ): IotizeBundleModel[] {
    return this.bundles().reduce(
      (list: IotizeBundleModel[], currentValue: IotizeBundleModel) => {
        if (currentValue.getACLGroup().hasRight(profileName, 'read')) {
          list.push(currentValue);
        }
        return list;
      },
      [],
    );
  }

  /**
   * Get an array of all variables
   */
  public variables(): IotizeVariableModel[] {
    if (!this.data.IOTizeConfiguration.IOTizeBundle) {
      return [];
    }
    let res: IotizeVariableModel[] = [];
    for (let bundle of this.data.IOTizeConfiguration.IOTizeBundle) {
      bundle = new IotizeBundleModel(bundle, this);
      res.push(...bundle.variables());
    }
    return res;
  }

  /**
   * Get bundle by id, if it does not exist, throw BundleDoesNotExistError
   * @throws BundleDoesNotExistError
   * @param id
   */
  public bundle(id: string): IotizeBundleModel {
    for (let bundle of this.bundles()) {
      if (bundle.id() === id) {
        return bundle;
      }
    }
    throw new BundleDoesNotExistError(id, this);
  }

  public hasBundle(id: string): IotizeBundleModel | false {
    for (let bundle of this.bundles()) {
      if (bundle.id() === id) {
        return bundle;
      }
    }
    return false;
  }

  /**
   * Get variable by id, if it does not exist, throw VariableDoesNotExistError
   * @throws VariableDoesNotExistError
   * @param id
   */
  public variable(variableId: string): IotizeVariableModel {
    for (let variable of this.variables()) {
      if (variable.id() == variableId) {
        return variable;
      }
    }
    throw new VariableDoesNotExistError(variableId, this);
  }

  /**
   * Get an array of all profiles
   */
  public profiles(): IotizeProfileModel[] {
    if (!this.data.IOTizeConfiguration.IOTizeProfile) {
      return [];
    }
    let profiles = [];
    for (let profile of this.data.IOTizeConfiguration.IOTizeProfile) {
      // TODO bug if there is only one profile...
      profiles.push(new IotizeProfileModel(profile));
    }
    return profiles;
  }

  public description(): string {
    return this.getAppConfig().$.GUI_Description;
  }

  public name(): string {
    return this.getAppConfig().$.GUI_Name;
  }
}
